import React, { PropsWithChildren, useCallback } from 'react';
import { find, join } from 'lodash';
import TdNew from '../../../core/components/table-components/table-new/TdNew';
import Tr from '../../../core/components/table-components/Tr';
import { UnitOfMeasure } from '../../../common/components/dictionary';
import { StockProductLink } from '../../common/components/StockProductLink';
import { toPoundCurrency } from '../../../common/utils/formatUtils';
import { PartRequestBulkItemDto } from '../types';
import { ColDef } from './columns';
import { StockBulkEditCellControl } from './StockBulkEditCellControl';

interface StockBulkTableRowProps {
  row: PartRequestBulkItemDto;
  columns: ColDef[];
  onEditLocation?: (row: PartRequestBulkItemDto) => void;
  onEditOwner?: (row: PartRequestBulkItemDto) => void;
  ordinalNumber?: number;
}

export const StockBulkTableRow = ({
  row, children, columns, onEditLocation, onEditOwner, ordinalNumber,
}: PropsWithChildren<StockBulkTableRowProps>) => {
  const isVisible = useCallback((key: keyof PartRequestBulkItemDto) => {
    return !!find(columns, { dataIndex: key });
  }, [columns]);

  return (
    <Tr>
      {children}
      {isVisible('ordinalNumber') && <TdNew>
        {ordinalNumber ?? row.ordinalNumber}
      </TdNew>}

      {isVisible('customerName') && <TdNew>{row.customerName}</TdNew>}

      {isVisible('linkedEntityNumber') && <TdNew>{row.linkedEntityNumber}</TdNew>}

      {isVisible('productNumber') && <TdNew>
        <StockProductLink {...row}>
          {row.productNumber}
        </StockProductLink>
      </TdNew>}

      {isVisible('productName')
        && <TdNew>
          <StockProductLink {...row}>
            {row.productName}
          </StockProductLink>
        </TdNew>}

      {isVisible('requestedQuantity') && <TdNew>
        <UnitOfMeasure value={row.unitOfMeasure}>
          {row.requestedQuantity}
        </UnitOfMeasure>
      </TdNew>}

      {isVisible('purchaseOrderGenerationQuantity') && <TdNew>
        <UnitOfMeasure value={row.unitOfMeasure}>
          {row.purchaseOrderGenerationQuantity}
        </UnitOfMeasure>
      </TdNew>}

      {isVisible('costPrice') && <TdNew>
        {toPoundCurrency(row.costPrice)}
      </TdNew>}

      {isVisible('suppliers') && <TdNew>
        {join(row.suppliers, ', ')}
      </TdNew>}

      {isVisible('pricelists') && <TdNew>
        {join(row.pricelists, ', ')}
      </TdNew>}

      {isVisible('availableInStock') && <TdNew className={row.canQuantityBeCoveredByStock ? '' : 'highlight-cell'}>
        {row.availableInStock}
      </TdNew>}

      {isVisible('allocatedQuantity') && <TdNew>
        {row.allocatedQuantity}
      </TdNew>}

      {isVisible('readyQuantity') && <TdNew>
        {row.readyQuantity}
      </TdNew>}

      {isVisible('collectedQuantity') && <TdNew>
        {row.collectedQuantity}
      </TdNew>}

      {isVisible('location') && <TdNew>
        <StockBulkEditCellControl row={row} onEdit={onEditLocation} hint="Edit Location">
          {row.location}
        </StockBulkEditCellControl>
      </TdNew>}

      {isVisible('ownerId') && <TdNew>
        <StockBulkEditCellControl row={row} onEdit={onEditOwner} hint="Edit Owner">
          {row.ownerName}
        </StockBulkEditCellControl>
      </TdNew>}
    </Tr>
  );
};
